// ./src/components/features/Contacts/ContactChannels/ContactChannels.tsx
//
// A person's email and phone as ACTIONS, the two ways the lists show them (moved out of ContactsList, ROADMAP #170
// 2026-10-20, so the vendor people lists show contacts exactly as Contacts does — memory: a rule followed is not a rule
// enforced):
//   • `ContactChannels` — compact mailto / tel links, for a desktop table cell;
//   • `ContactChannelsMobile` — one row per channel with a big Call / Email button and a Copy button, for a card (fingers,
//     not tiny links). Every button stops propagation, so a tap never starts a swipe or toggles a selection.
import React from "react";
import { Stack, Group, Text, Anchor, ActionIcon, Tooltip, CopyButton } from "@mantine/core";
import { useTranslation } from "react-i18next";
import { formatPhone } from "@em/utils";

import { AppIcon } from "@/components/generic";

/** Anything with an email and a phone — a Contact, a vendor's person. */
export interface ContactChannelsSource {
  email?: string | null;
  phone?: string | null;
}

// Email + phone on their own lines as actionable links — clicking emails (mailto)
// or calls (tel) the contact directly from the list. stopPropagation keeps a tap
// from toggling row selection in mobile selection mode.
export const ContactChannels: React.FC<{ person: ContactChannelsSource }> = ({ person }) => {
  const email = (person.email || "").trim();
  const phone = (person.phone || "").trim();

  if (!email && !phone) {
    return (
      <Text size="sm" c="dimmed">
        —
      </Text>
    );
  }

  return (
    <Stack gap={2}>
      {email && (
        <Group gap={5} wrap="nowrap">
          <AppIcon iconName="Mail" size={13} />
          <Anchor href={`mailto:${email}`} size="md" lineClamp={1} onClick={(e) => e.stopPropagation()}>
            {email}
          </Anchor>
        </Group>
      )}
      {phone && (
        <Group gap={5} wrap="nowrap">
          <AppIcon iconName="Phone" size={13} />
          <Anchor href={`tel:${phone}`} size="md" onClick={(e) => e.stopPropagation()}>
            {formatPhone(phone)}
          </Anchor>
        </Group>
      )}
    </Stack>
  );
};

// A copy-to-clipboard action button (own tap target, own feedback). stopPropagation
// keeps it from toggling row selection / triggering a swipe.
const CopyChannelButton: React.FC<{ value: string; label: string }> = ({ value, label }) => {
  const { t } = useTranslation(["contacts", "common"]);

  return (
    <CopyButton value={value} timeout={1500}>
      {({ copied, copy }) => (
        <Tooltip label={copied ? t("common:copied", { defaultValue: "Copied" }) : `${t("common:copy", { defaultValue: "Copy" })} ${label}`} withArrow>
          <ActionIcon
            variant="light"
            color={copied ? "teal" : "gray"}
            size="lg"
            radius="md"
            onClick={(e) => {
              e.stopPropagation();
              copy();
            }}
            aria-label={`${t("common:copy", { defaultValue: "Copy" })} ${label}`}
          >
            <AppIcon iconName={copied ? "Check" : "Copy"} size={16} />
          </ActionIcon>
        </Tooltip>
      )}
    </CopyButton>
  );
};

// One email / phone row on mobile: value text + a big primary action (Call / Email)
// + a Copy button. Bigger, well-separated tap targets than the compact list links.
const ContactChannelRow: React.FC<{
  iconName: string;
  value: string;
  actionHref: string;
  actionIcon: string;
  actionColor: string;
  actionLabel: string;
  copyLabel: string;
}> = ({ iconName, value, actionHref, actionIcon, actionColor, actionLabel, copyLabel }) => (
  <Group gap="xs" wrap="nowrap">
    <AppIcon iconName={iconName} size={15} />
    <Text size="md" style={{ flex: 1, minWidth: 0 }} truncate>
      {value}
    </Text>
    <Tooltip label={actionLabel} withArrow>
      <ActionIcon
        component="a"
        href={actionHref}
        variant="light"
        color={actionColor}
        size="lg"
        radius="md"
        onClick={(e) => e.stopPropagation()}
        aria-label={actionLabel}
      >
        <AppIcon iconName={actionIcon} size={16} />
      </ActionIcon>
    </Tooltip>
    <CopyChannelButton value={value} label={copyLabel} />
  </Group>
);

// Mobile channels — one row per email / phone, each with explicit Call/Email + Copy
// buttons (fingers, not tiny links). Desktop keeps the compact ContactChannels.
export const ContactChannelsMobile: React.FC<{ person: ContactChannelsSource }> = ({ person }) => {
  const { t } = useTranslation(["contacts", "common"]);
  const email = (person.email || "").trim();
  const phone = (person.phone || "").trim();

  if (!email && !phone) {
    return null;
  }

  return (
    <Stack gap="xs">
      {phone && (
        <ContactChannelRow
          iconName="Phone"
          value={formatPhone(phone)}
          actionHref={`tel:${phone}`}
          actionIcon="Phone"
          actionColor="green"
          actionLabel={t("contacts:actions.callPerson", { defaultValue: "Call" })}
          copyLabel={t("contacts:channels.phone", { defaultValue: "phone" })}
        />
      )}
      {email && (
        <ContactChannelRow
          iconName="Mail"
          value={email}
          actionHref={`mailto:${email}`}
          actionIcon="Mail"
          actionColor="blue"
          actionLabel={t("contacts:actions.emailPerson", { defaultValue: "Email" })}
          copyLabel={t("contacts:channels.email", { defaultValue: "email" })}
        />
      )}
    </Stack>
  );
};
